import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/react";
import ButtonNew from "components/buttonNew/buttonNew";
import { Checkbox } from "components/ui/checkbox";

export type ReminderTenantDocuments = {
  id: string;
  name: string;
  unitId: string;
  documents: string[];
};

export interface MissingDocumentsReminderModalProps {
  isOpen: boolean;
  tenants: ReminderTenantDocuments[];
  selectedDocumentsByTenant: Record<string, Set<string>>;
  onClose: () => void;
  onConfirm: () => void;
  onToggleDocument: (tenantId: string, label: string) => void;
  onSelectAll: () => void;
  onClear: () => void;
}

export const MissingDocumentsReminderModal = ({
  isOpen,
  tenants,
  selectedDocumentsByTenant,
  onClose,
  onConfirm,
  onToggleDocument,
  onSelectAll,
  onClear,
}: MissingDocumentsReminderModalProps) => {
  const hasSelectedDocuments = Object.values(selectedDocumentsByTenant).some(
    (documents) => documents.size > 0,
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1 px-6 pt-6">
          <span className="text-lg font-semibold text-neutral-primary">
            Kies ontbrekende documenten
          </span>
          <span className="text-sm font-normal text-neutral-secondary-mid">
            Selecteer welke documenten in de herinneringsmail voor deze selectie
            worden meegenomen.
          </span>
        </ModalHeader>
        <ModalBody className="gap-4 px-6 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded border border-neutral-primary/15 bg-neutral-primary/[0.03] px-4 py-3 text-sm">
            <span className="font-medium text-neutral-primary">
              {tenants.length} geselecteerde huurders
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="rounded border border-neutral-primary/20 bg-white px-2.5 py-1 text-xs font-medium text-moss-primary transition hover:border-moss-primary/40"
                onClick={onSelectAll}
              >
                Alles selecteren
              </button>
              <button
                type="button"
                className="rounded border border-neutral-primary/20 bg-white px-2.5 py-1 text-xs font-medium text-neutral-secondary-mid transition hover:border-neutral-primary/40"
                onClick={onClear}
              >
                Wissen
              </button>
            </div>
          </div>

          <div className="grid max-h-[55vh] gap-3 overflow-y-auto pr-1">
            {tenants.map((tenant) => (
              <section
                key={tenant.id}
                className="rounded border border-neutral-primary/15 bg-white"
              >
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-neutral-primary/10 px-4 py-3">
                  <span className="min-w-0 truncate text-sm font-semibold text-neutral-primary">
                    {tenant.name || "Huurder zonder naam"}
                  </span>
                  {tenant.unitId && (
                    <span className="shrink-0 rounded-full bg-moss-primary/[0.08] px-2.5 py-1 text-xs font-medium text-moss-primary">
                      {tenant.unitId}
                    </span>
                  )}
                </div>
                <div className="grid gap-1 p-2">
                  {tenant.documents.map((document) => (
                    <label
                      key={document}
                      className="flex cursor-pointer items-center gap-3 rounded px-2 py-2 transition hover:bg-moss-primary/[0.03]"
                    >
                      <Checkbox
                        checked={
                          selectedDocumentsByTenant[tenant.id]?.has(document) ||
                          false
                        }
                        onCheckedChange={() =>
                          onToggleDocument(tenant.id, document)
                        }
                      />
                      <span className="text-sm font-medium text-neutral-primary">
                        {document}
                      </span>
                    </label>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </ModalBody>
        <ModalFooter className="gap-2 border-t border-neutral-primary/10 px-6 py-4">
          <ButtonNew
            variant="secondaryNeutral"
            label="Annuleren"
            onClick={onClose}
          />
          <button
            type="button"
            className="rounded bg-moss-primary px-4 py-2 text-sm font-medium text-white transition hover:bg-moss-primary/90 disabled:cursor-not-allowed disabled:bg-neutral-primary/20 disabled:text-neutral-secondary-mid"
            disabled={!hasSelectedDocuments}
            onClick={onConfirm}
          >
            Naar herinneringsmail
          </button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
